import React, { useEffect, useState, useRef } from 'react'
import { useAuth, parseProfileResponse } from '../hooks/getAuth'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'
import { callAPI } from '../hooks/api'
import { createConfirmation } from 'react-confirm'
import CancelSubscription from '../components/CancelSubscription'
import Spinner from '../components/Spinner'
import { updateUserNotifyHsChangeUsersNotifyHsChangePutMutation } from '../generated/@tanstack/react-query.gen'
import { useMutation } from '@tanstack/react-query'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import StripeChangePlan from '../components/StripeChangePlan'
import useMapStore from '@/hooks/use-map-store'
import { updateProfileUpdateProfilePostMutation } from '../generated/@tanstack/react-query.gen'
import { formatDate, formatDateTime } from '@/lib/utils'
import { format, fromUnixTime, isBefore } from 'date-fns'
import { siteConfig } from '@/config/site'
import { EFreeTrialStatusType } from '@/components/welcome-modal/welcome-modal'
import useAbility from '@/hooks/use-ability'

const stripeKeys = {
  staging:
    'pk_test_51QSNI4IO8znrKeTZhqhkcxw0blJszt4R2wItky3MwXFhZ2BCU1b5icVW2ktBhVe8exuMFPYU9Sr7s3mRsMBINyEU00vsfypbmy',
  dev: 'pk_test_51QG5OnIBFLOCRedmHsgRDt55gklVhd5I2iL0xmukrdWw93fr0lGLD6ClpnzU57ISwOnXNCWmFWxeASG7Cexc4JkX00MM2wnBwd',
  development:
    'pk_test_51QSNI4IO8znrKeTZhqhkcxw0blJszt4R2wItky3MwXFhZ2BCU1b5icVW2ktBhVe8exuMFPYU9Sr7s3mRsMBINyEU00vsfypbmy',
  production:
    'pk_live_51QG5OnIBFLOCRedmxh46nUVOkKdmUlLKA1bDketitKcgHH6fCzrfw18BWGL3UbHZ6vzQqsD2KMnsUipXRTnLFm22004gPbl6Qc',
}

const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'staging'
const stripePK = stripeKeys[env] || stripeKeys.staging

const stripePromise = loadStripe(stripePK)

export default function Profile({
  currentTab,
  setCurrentTab,
  setJoyrideRun,
  localAuth,
}) {
  const removeStylesheet = (href) => {
    const link = document.querySelector(`link[href="${href}"]`)
    if (link) {
      document.body.removeChild(link)
    }
  }
  removeStylesheet('/css/landing.css')
  removeStylesheet('/css/bootstrap.css')
  const [searchParams] = useSearchParams()
  const selectRef = useRef(null)

  const [loading, setLoading] = useState(false)
  const { auth, setAuth, setIsLoggedIn } = useAuth()
  const [tierStatus, setTierStatus] = useState('')
  const [quota, setQuota] = useState(0)
  const confirm = createConfirmation(CancelSubscription)

  const { resetStore } = useMapStore()

  const {
    getAbilityLimit,
    getAbilityOptionalLimit,
  } = useAbility()

  useEffect(() => {
    getPlanStatus()
  }, [auth])

  useEffect(() => {
    setCurrentTab('profile')
  }, [])

  const getPlanStatus = () => {
    if (auth && auth.name) {
      setLoading(true)
      callAPI('/get_plan_status', { session_key: auth.sessionKey }).then(
        (res) => {
          setLoading(false)
          if (res.status === 'success') {
            setTierStatus(res.plan_status)
            setFormData({
              name: auth.name,
              email: auth.email,
              sessionKey: auth.sessionKey,
              tier: auth.tier + ` (${res.plan_status})`,
              oldPassword: '',
              newPassword: '',
            })
          } else {
            setFormData({
              name: auth.name,
              email: auth.email,
              sessionKey: auth.sessionKey,
              tier: auth.tier,
              oldPassword: '',
              newPassword: '',
            })
          }
        }
      )
    }
  }

  const navigate = useNavigate()

  // Define the tiers available for subscription plans.
  const tiers = {
    Advanced: 'Advanced ($10 / mo)',
    'Advanced-annual': 'Advanced - Annually ($96 / yr)',
    Professional: 'Professional ($100 / mo)',
    'Professional-annual': 'Professional - Annually ($960 / yr)',
  }

  const nextPhaseTexts = {
    renew: 'renews',
    cancel: 'will be cancelled',
    update: 'will be updated',
  }
  const currentPlan = localAuth?.tier?.includes(' - ')
    ? localAuth.tier.split(' - ')[0]
    : localAuth.tier

  const isDemoUser =
    localAuth.sessionKey === 'a6cf9f6e-3341-4996-8d6a-83e826b67adf'
  const isBasicUser = currentPlan === 'Basic'
  const isTrialUser = currentPlan === 'Free Trial'
  const isEnterpriseUser = currentPlan === 'Enterprise'
  const isHydraScoreAdmin = currentPlan === 'Hydra Score'
  const renewalDate = localAuth?.renewal_date ? format(new Date(localAuth?.renewal_date), siteConfig.dateFormat) : undefined
  const nextPhase = localAuth?.next_phase ? localAuth?.next_phase : undefined
  const isInAppPurchase = localAuth?.iap_platform === 'iOS' || localAuth?.iap_platform === 'Android'
  const platformStore = localAuth?.iap_platform === 'iOS' ? 'Apple app store' : 'Google Play store'
  const renewalMessage = isInAppPurchase ? `This plan renews automatically on the ${platformStore} on ${renewalDate}` : `This plan ${nextPhase ? nextPhaseTexts[nextPhase] : 'renews' } automatically on ${renewalDate}`

  const isExpired =
    localAuth?.trial_status === EFreeTrialStatusType.EXPIRED_TRIAL ||
    (localAuth?.trial_end_at && isBefore(new Date(localAuth.trial_end_at), new Date()))

  const numberOfReports = isExpired ? 0 : getAbilityLimit('number_of_reports')
  const numberOfRemainingReports = isExpired ? 0 : getAbilityOptionalLimit(
    'number_of_remaining_reports'
  )
  const getRemainingReports = (numberOfRemainingReports, numberOfReports) => {
    if (numberOfReports === -1) {
      return 'Infinite';
    }
    if (numberOfRemainingReports === 0 && numberOfReports === 0) {
      return '0';
    }
    return `${numberOfRemainingReports}/${numberOfReports}`;
  }

  const [changePlan, setChangePlan] = useState(
    searchParams.get('upgrade') != null
  )
  const [selectedPlan, setSelectedPlan] = useState(
    tiers[currentPlan] ? currentPlan : 'Advanced'
  )

  const [changePassword, setChangePassword] = useState(false)
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (changePlan && selectRef.current) {
      selectRef.current.focus()
      selectRef.current.click()
    }
  }, [changePlan, tiers])

  const [formData, setFormData] = useState({
    name: localAuth?.name,
    email: localAuth?.email,
    tier: localAuth?.tier,
    sessionKey: '',
    password: '',
    oldPassword: '',
    newPassword: '',
  })

  const { name, email, sessionKey, tier, password, oldPassword, newPassword } = formData

  function onLogout() {
    localStorage.setItem('auth', JSON.stringify({}))
    resetStore()
    setAuth(null)
    setIsLoggedIn(false)
    window.location.replace('/sign-in')
  }

  function onSignUp() {
    localStorage.setItem('auth', JSON.stringify({}))
    setAuth(null)
    setIsLoggedIn(false)
    window.location.replace('/sign-in')
  }

  function deleteAccount() {
    var endpoint = '/delete_account'
    var params = {
      email: email,
      password: password,
      session_key: sessionKey,
    }
    setLoading(true)
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'fail') {
        setConfirmDelete(true)
        toast.warn(res.msg)
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        onLogout()
      } else {
        toast.success(res.msg)
        onLogout()
      }
    })
  }

  function updatePassword() {
    var endpoint = '/change_password_with_session_key'
    var params = {
      old_password: oldPassword,
      password: newPassword,
      session_key: sessionKey,
    }
    setLoading(true)
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'fail') {
        setChangePassword(true)
        toast.warn(res.msg)
      } else if (res.status === 'expire') {
        toast.warn(res.msg)
        onLogout()
      } else {
        const userCredential = parseProfileResponse(res)
        if (userCredential.sessionKey) {
          localStorage.setItem('auth', JSON.stringify(userCredential))
          setAuth(userCredential)
          setIsLoggedIn(true)
          toast.success('Updated')
        }
      }
    })
  }

  function onChange(e) {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }))
  }

  const [enableStartupTutorial, setEnableStartupTutorial] = useState(
    'tutorial' in JSON.parse(localStorage.getItem('auth'))
      ? JSON.parse(localStorage.getItem('auth')).tutorial
      : true
  )

  const handleTutorialChange = (e) => {
    var endpoint = '/set_tutorial'
    var params = {
      tutorial: !enableStartupTutorial,
      session_key: sessionKey,
    }
    setLoading(true)
    callAPI(endpoint, params).then((res) => {
      setLoading(false)
      if (res.status === 'success') {
        let temp = JSON.parse(localStorage.getItem('auth'))
        temp.tutorial = !enableStartupTutorial
        localStorage.setItem('auth', JSON.stringify(temp))
        setJoyrideRun(!enableStartupTutorial)
      }
    })
    setEnableStartupTutorial(!enableStartupTutorial)
  }

  const handlePlanChange = (e) => {
    setSelectedPlan(e.target.value)
  }
  return (
    <>
      {loading && <Spinner />}
      <section className="max-w-6xl mx-auto flex justify-center items-center flex-col">
        <h1 className="text-3xl text-center mt-6 font-bold">My Profile</h1>
        <div className="w-full md:w-[50%] mt-6 px-3">
          <form>
            {/* Name Input */}
            <div className="mb-2">
              <label
                htmlFor="name"
                className="block text-xl text-gray-700 mb-2"
              >
                Full Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                disabled={true}
                className="w-full px-4 py-2 text-xl text-gray-700 bg-gray-200 border border-gray-300 rounded transition ease-in-out"
              />
            </div>

            {/* Email Input */}
            <div className="mb-2">
              <label
                htmlFor="email"
                className="block text-xl text-gray-700 mb-2"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                disabled={true}
                className="w-full px-4 py-2 text-xl text-gray-700 bg-gray-200 border border-gray-300 rounded transition ease-in-out"
              />
            </div>

            {/* Tier Input */}
            <div className="mb-2">
              <label
                htmlFor="tier"
                className="block text-xl text-gray-700 mb-2"
              >
                Plan
              </label>
              <input
                type="text"
                id="tier"
                value={
                  tier.includes('annual')
                  ? tier.replace('-annual', ' - Annually')
                  : (tier ?? '')
                }
                disabled={true}
                className="w-full px-4 py-2 text-xl text-gray-700 bg-gray-200 border border-gray-300 rounded transition ease-in-out"
              />
            </div>

            {/* Reports Remaining */}
            <div className="mb-2">
              <label
                htmlFor="tier"
                className="block text-xl text-gray-700 mb-2"
              >
                Reports Remaining
              </label>
              <input
                type="text"
                id="tier"
                value={getRemainingReports(numberOfRemainingReports, numberOfReports)}
                disabled={true}
                className="w-full px-4 py-2 text-xl text-gray-700 bg-gray-200 border border-gray-300 rounded transition ease-in-out"
              />
            </div>

            {/* Renewal Date */}
            {renewalDate && (
              <div className="mb-2 text-sm text-destructive">
                {renewalMessage}
              </div>
            )}

            {/* Trial End Date */}
            {isTrialUser && !isExpired && (
              <div className="mb-2 text-sm text-destructive">
                Your trial ends on {formatDate(localAuth?.trial_end_at)}
              </div>
            )}

            {/* Trial Expired */}
            {isTrialUser && isExpired && (
              <div className="mb-2 text-sm text-destructive">
                Your trial has expired. Please upgrade your plan.
              </div>
            )}

            {isInAppPurchase && (
              <div className="mb-2 font-bold">
                Please log in to the <span className="text-primary">Hydra Score</span> mobile app to manage your plan.
              </div>
            )}

            {/* Change Plan */}
            {!isDemoUser &&
              !isEnterpriseUser &&
              !isHydraScoreAdmin &&
              !isInAppPurchase && (
              <div className="flex text-center justify-between whitespace-nowrap text-xl text-gray-700 mb-2">
                <div className="flex text-center justify-between">
                  <p className="flex items-center">
                    Change your plan?
                    <span
                      onClick={async () => {
                        setChangePlan((prevState) => !prevState)
                      }}
                      className="text-green-600 hover:text-green-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
                    >
                      {changePlan ? 'Cancel' : 'Change'}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {changePlan &&
              !isEnterpriseUser &&
              !isHydraScoreAdmin &&
              !isInAppPurchase && (
              <select
                ref={selectRef}
                value={selectedPlan}
                className="mb-2 w-full px-4 py-2 text-base text-gray-700 bg-white border-gray-300 rounded transition ease-in-out"
                onChange={handlePlanChange}
              >
                {Object.entries(tiers).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            )}

            {changePlan &&
              currentPlan !== selectedPlan &&
              !isEnterpriseUser &&
              !isHydraScoreAdmin &&
              !isInAppPurchase && (
                <Elements stripe={stripePromise}>
                  <StripeChangePlan email={email} newPlan={selectedPlan} cardRequired={isTrialUser || isBasicUser}/>
                </Elements>
              )}

            {/* Cancel Plan */}
            {!isDemoUser &&
              !isTrialUser &&
              !isBasicUser &&
              !isEnterpriseUser &&
              !isHydraScoreAdmin &&
              !isInAppPurchase && (
                <div className="flex text-center justify-between whitespace-nowrap text-xl text-gray-700 mb-2">
                  <div className="flex text-center justify-between">
                    <p className="flex items-center">
                      Cancel your plan?
                      <span
                        onClick={async () => {
                          const proceed = await confirm({
                            confirmation: `Once canceled, this action cannot be undone. Do you wish to continue? (Your current plan will remain active until the end of your billing cycle.)`,
                          })
                          if (proceed) {
                            var endpoint = '/cancel_plan'
                            var params = { session_key: sessionKey }
                            setLoading(true)
                            callAPI(endpoint, params).then((res) => {
                              setLoading(false)
                              if (res.status === 'success') {
                                const userCredential = parseProfileResponse(res)
                                if (userCredential.sessionKey) {
                                  localStorage.setItem(
                                    'auth',
                                    JSON.stringify(userCredential)
                                  )
                                  setAuth(userCredential)
                                  setIsLoggedIn(true)
                                  toast.success(
                                    'Your subscription cancellation request is submitted. Please allow 1~5 minutes.'
                                  )
                                }
                              } else if (res.status === 'expire') {
                                toast.warn(res.msg)
                                onLogout()
                              } else {
                                toast.warn(res.msg)
                              }
                            })
                          }
                        }}
                        className="text-red-600 hover:text-red-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
                      >
                        Cancel
                      </span>
                    </p>
                  </div>
                </div>
              )}

            {/* Tutorial upon startup */}
            <div className="mb-2 flex items-center text-center">
              <label
                htmlFor="tutorialStartup"
                className="flex items-center text-xl text-gray-700"
              >
                <span className="ml-auto">
                  Show tutorial upon startup&nbsp;
                </span>
                <input
                  type="checkbox"
                  id="tutorialStartup"
                  checked={enableStartupTutorial}
                  onChange={handleTutorialChange}
                />
              </label>
            </div>

            {/* Send email when Score change */}
            <SendEmailScoreChangeCheckBox setLoading={setLoading} />

            {/* Request a new feature */}
            <div className="flex text-center justify-between whitespace-nowrap text-xl mb-2 hidden">
              <div className="flex text-center justify-between">
                Request a new feature?{' '}
                <a
                  href="https://hydrascore.canny.io/feature-requests"
                  target="_blank"
                  rel="noreferrer"
                  className="ml-1 text-blue-600 hover:text-blue-700 transition ease-in-out duration-200"
                >
                  Request
                </a>
              </div>
            </div>

            {/* Update profile */}
            {!isDemoUser && <UpdateProfile setLoading={setLoading} />}

            {/* Change password */}
            {!isDemoUser && (
              <div className="flex text-center justify-between whitespace-nowrap text-xl text-gray-700 mb-2">
                <div className="flex text-center w-full justify-between">
                  <p className="flex items-center">
                    Update your password?
                    <span
                      onClick={() => {
                        if (changePassword) {
                          updatePassword()
                        }
                        setChangePassword((prevState) => !prevState)
                      }}
                      className="text-blue-600 hover:text-blue-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
                    >
                      {changePassword ? 'Submit' : 'Update'}
                    </span>
                  </p>
                  {changePassword && (
                    <span
                      onClick={() => {
                        setChangePassword(false)
                      }}
                      className="text-red-600 hover:text-red-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
                    >
                      Cancel
                    </span>
                  )}
                </div>
              </div>
            )}

            {changePassword && (
              <>
                <div className="relative mt-4 mb-2">
                  <label
                    htmlFor="oldPassword"
                    className="block text-xl text-gray-700 mb-2"
                  >
                    Old Password
                  </label>
                  <input
                    className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
                    type={showOldPassword ? 'text' : 'password'}
                    id="oldPassword"
                    value={oldPassword}
                    onChange={onChange}
                  />
                  {showOldPassword ? (
                    <AiFillEyeInvisible
                      className="absolute right-3 top-12 text-xl cursor-pointer"
                      onClick={() =>
                        setShowOldPassword((prevState) => !prevState)
                      }
                    />
                  ) : (
                    <AiFillEye
                      className="absolute right-3 top-12 text-xl cursor-pointer"
                      onClick={() =>
                        setShowOldPassword((prevState) => !prevState)
                      }
                    />
                  )}
                </div>
                <div className="relative mb-4">
                  <label
                    htmlFor="newPassword"
                    className="block text-xl text-gray-700 mb-2"
                  >
                    New Password
                  </label>
                  <input
                    className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={onChange}
                  />
                  {showNewPassword ? (
                    <AiFillEyeInvisible
                      className="absolute right-3 top-12 text-xl cursor-pointer"
                      onClick={() =>
                        setShowNewPassword((prevState) => !prevState)
                      }
                    />
                  ) : (
                    <AiFillEye
                      className="absolute right-3 top-12 text-xl cursor-pointer"
                      onClick={() =>
                        setShowNewPassword((prevState) => !prevState)
                      }
                    />
                  )}
                </div>
              </>
            )}

            {/* Delete a user */}
            {!isDemoUser && (
              <div className="flex text-center justify-between whitespace-nowrap text-xl text-gray-700 mb-2">
                <div className="flex text-center w-full justify-between">
                  <p className="flex items-center">
                    Delete your account?{' '}
                    <span
                      onClick={() => {
                        if (confirmDelete) {
                          deleteAccount()
                        }
                        setConfirmDelete((prevState) => !prevState)
                      }}
                      className="ml-1 text-red-600 hover:text-red-700 transition ease-in-out duration-200 cursor-pointer"
                    >
                      {confirmDelete ? 'Confirm' : 'Delete'}
                    </span>
                  </p>

                  {confirmDelete && (
                    <span
                      onClick={() => {
                        setConfirmDelete(false)
                      }}
                      className="text-green-600 hover:text-green-700 transition ease-in-out duration-200 ml-2 cursor-pointer"
                    >
                      Cancel
                    </span>
                  )}
                </div>
                {!confirmDelete && (
                  <p
                    onClick={onLogout}
                    className="text-blue-600 hover:text-blue-800 transition duration-200 ease-in-out cursor-pointer"
                  >
                    Sign out
                  </p>
                )}
              </div>
            )}

            {confirmDelete && (
              <>
                <div className="relative mb-2 text-warning">
                  Deleting your account will remove all personal data associated with your account, except data required to meet legal obligations.
                </div>
                <div className="relative mb-2">
                  To permanently delete your account, please sign in and confirm.
                </div>
                <div className="relative mb-2">
                  <label
                    htmlFor="email"
                    className="block text-xl text-gray-700 mb-2"
                  >
                    Email
                  </label>
                  <input
                    className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
                    type="email"
                    id="email"
                    value={email}
                    onChange={onChange}
                  />
                </div>
                <div className="relative mb-4">
                  <label
                    htmlFor="password"
                    className="block text-xl text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <input
                    className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={onChange}
                  />
                  {showNewPassword ? (
                    <AiFillEyeInvisible
                      className="absolute right-3 top-12 text-xl cursor-pointer"
                      onClick={() =>
                        setShowPassword((prevState) => !prevState)
                      }
                    />
                  ) : (
                    <AiFillEye
                      className="absolute right-3 top-12 text-xl cursor-pointer"
                      onClick={() =>
                        setShowPassword((prevState) => !prevState)
                      }
                    />
                  )}
                </div>
              </>
            )}

            {confirmDelete && (
              <div className="w-full flex justify-end mt-4 mb-8">
                <div
                  onClick={onLogout}
                  className="text-sm sm:text-lg text-blue-600 hover:text-blue-800 transition duration-200 ease-in-out cursor-pointer"
                >
                  Sign out
                </div>
              </div>
            )}

            {/* Sign Up */}
            {isDemoUser && (
              <div className="flex text-center justify-between whitespace-nowrap text-xl text-gray-700 mb-2">
                <div className="flex text-center justify-between">
                  <p className="flex items-center">
                    Sign Up as a new user?
                    <span
                      onClick={() => {
                        onSignUp()
                      }}
                      className="text-blue-600 hover:text-blue-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
                    >
                      Sign Up
                    </span>
                  </p>
                </div>
                <p
                  onClick={onLogout}
                  className="text-blue-600 hover:text-blue-800 transition duration-200 ease-in-out cursor-pointer"
                >
                  Sign out
                </p>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  )
}

const UpdateProfile = ({ setLoading, onLogout }) => {
  const { auth, setAuth } = useAuth()

  const [showUpdate, setShowUpdate] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })

  useEffect(() => {
    setFormData({
      firstName: auth?.firstname,
      lastName: auth?.lastname,
      email: auth?.email,
    })
  }, [auth])

  const { mutate } = useMutation({
    ...updateProfileUpdateProfilePostMutation({}),
    onMutate: () => setLoading(true),
    onSuccess: (response) => {
      if (response.status === 'fail') {
        return toast.warn(response.msg)
      }

      if (response.status === 'expire') {
        toast.warn(response.msg)
        return onLogout()
      }

      const authUser = localStorage.getItem('auth')
      const jsonAuthUser = JSON.parse(authUser)
      const newAuthUser = {
        ...jsonAuthUser,
        firstname: response.data.firstname,
        lastname: response.data.lastname,
        name: response.data.fullname,
        email: response.data.email,
      }
      setAuth(newAuthUser)
      localStorage.setItem('auth', JSON.stringify(newAuthUser))
      setShowUpdate((prevState) => !prevState)
      toast.success('Successfully updated profile detail.')
    },
    onError: (error) =>
      toast.error(`Failed to update: ${error?.message || 'Unknown error'}`),
    onSettled: () => setLoading(false),
  })

  const handleUpdate = () => {
    mutate({
      body: {
        firstname: formData.firstName,
        lastname: formData.lastName,
        email: formData.email,
        session_key: auth.sessionKey,
      },
    })
  }

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }))
  }

  return (
    <div className="flex flex-col text-center justify-between whitespace-nowrap text-xl text-gray-700 mb-2">
      <div className="flex text-center justify-between">
        <p className="flex items-center">
          Update your profile?
          <span
            onClick={() => {
              if (showUpdate) {
                handleUpdate()
                return
              }
              setShowUpdate((prevState) => !prevState)
            }}
            className="text-blue-600 hover:text-blue-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
          >
            {showUpdate ? 'Submit' : 'Update'}
          </span>
        </p>
        {showUpdate && (
          <span
            onClick={() => {
              setShowUpdate(false)
            }}
            className="text-red-600 hover:text-red-700 transition ease-in-out duration-200 ml-1 cursor-pointer"
          >
            Cancel
          </span>
        )}
      </div>

      {showUpdate && (
        <div className="my-4">
          <div className="mb-2">
            <label
              htmlFor="firstName"
              className="block text-xl text-gray-700 mb-2 text-start"
            >
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
            />
          </div>
          <div className="mb-2">
            <label
              htmlFor="lastName"
              className="block text-xl text-gray-700 mb-2 text-start"
            >
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
            />
          </div>
          <div className="mb-2 ">
            <label
              htmlFor="email"
              className="block text-xl text-gray-700 mb-2 text-start"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 text-xl text-gray-700 bg-white border border-gray-300 rounded transition ease-in-out"
            />
          </div>
        </div>
      )}
    </div>
  )
}

const SendEmailScoreChangeCheckBox = ({ setLoading }) => {
  const [isChecked, setIsChecked] = useState(false)

  const { mutate, isPending } = useMutation({
    ...updateUserNotifyHsChangeUsersNotifyHsChangePutMutation({}),
    onSuccess: () => {
      setLoading(false)
      saveHsChangeToLocalStorage()
    },
    onError: (error) => {
      setIsChecked(!isChecked)
      setLoading(false)
      if (error?.code === 'ERR_BAD_REQUEST') {
        toast.warn(
          'You are not subscribed. If the issue persists, contact info@hydrascore.com.'
        )
      } else {
        toast.error(`Failed to update: ${error?.message || 'Unknown error'}`)
      }
    },
  })

  function saveHsChangeToLocalStorage() {
    const authUser = localStorage.getItem('auth')
    if (!authUser || !JSON.parse(authUser)) return
    localStorage.setItem(
      'auth',
      JSON.stringify({ ...JSON.parse(authUser), notifyHsChange: isChecked })
    )
  }

  function getHsChangeFromLocalStorage() {
    const authUser = localStorage.getItem('auth')
    if (!authUser || !JSON.parse(authUser)) return
    return JSON.parse(authUser).notifyHsChange
  }

  useEffect(() => {
    const notifyHsChange = getHsChangeFromLocalStorage() ?? false
    setIsChecked(notifyHsChange)
  }, [])

  return (
    <div className="mb-2">
      <label
        htmlFor="send-email-score-changes"
        className="flex flex-row items-center justify-start w-fit text-xl text-gray-700"
      >
        <span className="ml-auto mr-2 text-start">
          Get emails for Hydra Score changes
        </span>
        <input
          type="checkbox"
          id="send-email-score-changes"
          checked={isChecked}
          onChange={(e) => {
            const value = e.target.checked
            setIsChecked(value)
            setLoading(true)
            mutate({
              body: {
                notify_hs_change: value,
              },
            })
          }}
        />
      </label>
    </div>
  )
}
